import React, { useEffect, useRef } from "react";
import Footer from "./Footer";
import Navbar from "./Navbar";
// import BankGradeCard from "./BankGradeCard";
import { FaShieldAlt, FaRocket, FaEye, FaBullseye, FaUsers } from "react-icons/fa";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SEO from "./SEO";

gsap.registerPlugin(ScrollTrigger);

const AboutUs: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sectionsRef = useRef<(HTMLElement | null)[]>([]);

  const companyName = import.meta.env.VITE_COMPANY_NAME || "the Company";

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power4.out" } });

      tl.from(".terminal-line", {
        scaleX: 0,
        duration: 1.5,
        transformOrigin: "left"
      })
        .from(".hero-title", {
          y: 100,
          opacity: 0,
          duration: 1.2,
          skewY: 7,
        }, "-=0.5")
        .from(".hero-badge", {
          opacity: 0,
          y: 20,
          duration: 0.8
        }, "-=0.8")
        .from(".hero-subtext", {
          y: 30,
          opacity: 0,
          duration: 1,
        }, "-=0.6");

      sectionsRef.current.forEach((section) => {
        if (section) {
          gsap.from(section, {
            y: 50,
            opacity: 0,
            duration: 1,
            scrollTrigger: {
              trigger: section,
              start: "top 85%",
            }
          });
        }
      });

      // Scanning Line Animation
      gsap.to(".scanning-line", {
        top: "100%",
        duration: 4,
        repeat: -1,
        ease: "none"
      });
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={containerRef} className="min-h-screen bg-white text-text-primary font-sans selection:bg-primary-light selection:text-primary overflow-x-hidden">
      <SEO
        title="About Us"
        description={`Learn about ${companyName}, an RBI-registered NBFC building transparent, accessible lending for individuals and businesses.`}
        keywords={`about ${companyName}, financial inclusion, NBFC, personal loan, business loan, mission vision`}
      />
      
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden bg-gradient-to-b from-white via-blue-50/50 to-white" />
      
      <div className="relative z-20">
        <Navbar />
      </div>

      <div className="relative z-10">
        {/* Soft Corporate Hero Section */}
        <section className="w-full pt-40 pb-20 flex flex-col items-center justify-center px-6 lg:px-12 relative overflow-hidden">
          <div className="container max-w-7xl flex flex-col items-center text-center">
            <div className="hero-badge flex flex-col items-center gap-4 mb-8">
              <div className="flex items-center gap-3 px-5 py-2 bg-white border border-blue-100 shadow-sm rounded-full">
                <div className="w-2.5 h-2.5 bg-primary rounded-full animate-pulse" />
                <span className="text-xs font-bold tracking-widest text-text-secondary uppercase">Corporate Profile</span>
              </div>
            </div>

            <h1 className="hero-title text-4xl sm:text-6xl md:text-8xl font-extrabold tracking-tight mb-8">
              About <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Us.</span>
            </h1>

            <p className="hero-subtext text-lg md:text-2xl text-text-secondary max-w-3xl leading-relaxed font-medium mt-4">
              We're an RBI-registered NBFC on a mission to make credit simple, transparent, and accessible for everyone.
              <span className="text-primary font-bold mt-4 block">{companyName} Team</span>
            </p>
          </div>
        </section>

        {/* Narrative Section */}
        <section ref={(el) => { sectionsRef.current[0] = el; }} className="w-full py-20 bg-white text-text-primary relative group border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-8">
                <div className="px-5 py-2 inline-block bg-blue-50 text-primary border border-blue-100 text-xs font-bold uppercase tracking-widest rounded-full">
                  Our Background
                </div>
                <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight leading-tight">
                  Built On <br /><span className="text-primary">Trust & Transparency.</span>
                </h2>
                <div className="space-y-6 border-l-4 border-primary pl-6">
                  <p className="text-xl md:text-2xl font-bold leading-tight text-text-primary">
                    {companyName} is a registered Non-Banking Financial Company (NBFC).
                  </p>
                  <p className="text-lg text-text-secondary leading-relaxed">
                    Our team of experienced financial professionals is focused on one thing: tailored, hassle-free loans for real people and real businesses.
                  </p>
                </div>
              </div>
              <div className="bg-blue-50/50 p-10 md:p-16 rounded-[2rem] border border-blue-100 shadow-sm relative overflow-hidden mt-10">
                <p className="text-text-primary text-lg font-bold leading-relaxed mb-8">
                  Our goal is simple — affordable, accessible credit for individuals and businesses, especially those underserved by traditional banks.
                </p>
                <p className="text-text-secondary text-base leading-relaxed italic border-l-2 border-primary/20 pl-6">
                  We believe financial inclusion drives economic growth and stability, and we're committed to helping every client reach their financial goals.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision Modules */}
        <section ref={(el) => { sectionsRef.current[1] = el; }} className="w-full py-24 bg-gray-50 text-text-primary">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Mission */}
              <div className="bg-white p-12 md:p-20 rounded-[2rem] border border-gray-100 shadow-sm transition-all duration-300 hover:shadow-md">
                <div className="flex items-center gap-4 mb-10">
                  <div className="w-14 h-14 bg-primary/10 flex items-center justify-center rounded-2xl">
                    <FaBullseye className="text-primary text-2xl" />
                  </div>
                  <div className="text-text-secondary text-xs font-bold uppercase tracking-widest">The Objective</div>
                </div>
                <h3 className="text-4xl md:text-5xl font-extrabold mb-8 text-text-primary tracking-tight">Our <br /><span className="text-primary">Mission.</span></h3>
                <p className="text-text-secondary text-xl font-medium leading-relaxed">
                  To deliver affordable, accessible loans for everyone — empowering individuals and businesses to reach their goals and fuel economic growth.
                </p>
              </div>

              {/* Vision */}
              <div className="bg-white p-12 md:p-20 rounded-[2rem] border border-gray-100 shadow-sm transition-all duration-300 hover:shadow-md">
                <div className="flex items-center gap-4 mb-10">
                  <div className="w-14 h-14 bg-secondary/10 flex items-center justify-center rounded-2xl">
                    <FaEye className="text-secondary text-2xl" />
                  </div>
                  <div className="text-text-secondary text-xs font-bold uppercase tracking-widest">The Future</div>
                </div>
                <h3 className="text-4xl md:text-5xl font-extrabold mb-8 text-text-primary tracking-tight">Our <br /><span className="text-secondary">Vision.</span></h3>
                <p className="text-text-secondary text-xl font-medium leading-relaxed">
                  To become one of India's most trusted NBFCs — driving financial inclusion and building a more secure future for our clients and communities.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Culture / Team Section */}
        <section ref={(el) => { sectionsRef.current[2] = el; }} className="w-full py-24 bg-white text-text-primary overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
            <div className="flex flex-col items-center gap-4 mb-16">
              <div className="text-primary font-bold text-xs uppercase tracking-widest bg-blue-50 px-4 py-2 rounded-full border border-blue-100">Core Values</div>
              <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-none text-text-primary">
                What Drives <br /><span className="text-secondary">Everything We Do.</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { title: "Transparency", icon: <FaShieldAlt />, desc: "Complete clarity in every transaction." },
                { title: "Efficiency", icon: <FaRocket />, desc: "Fast decisions and quick disbursals when you need them most." },
                { title: "Inclusion", icon: <FaUsers />, desc: "Extending credit to those overlooked by traditional banking." }
              ].map((item, i) => (
                <div key={i} className="bg-white border border-gray-100 shadow-sm rounded-3xl p-10 hover:border-primary hover:shadow-lg transition-all duration-300 group text-left">
                  <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center text-primary text-2xl mb-8 group-hover:scale-110 group-hover:bg-primary group-hover:text-white transition-all">{item.icon}</div>
                  <h4 className="text-2xl font-bold mb-4 text-text-primary">{item.title}</h4>
                  <p className="text-text-secondary leading-relaxed font-medium">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </div>
  );
};

export default AboutUs;
